// Edit-and-resend with versions, the way Claude and Gemini do it: editing a prompt keeps the old
// prompt and everything after it as a version the user can flip back to, rather than throwing it
// away. Nothing here is thrown away because a version may hold contacts the user paid for.
//
// `chat.messages` stays the live path, a plain array, so every flow that appends to it or updates
// a message by id keeps working untouched. The versions that are not showing live beside it:
//
//   chat.branches = { [anchorId]: { versions: [{ messages, ctx }], active } }
//
// anchorId is the id of the message just before the forked user message ('root' for the first
// message). versions[active] is always { messages: null, ctx: null }: the live version is whatever
// sits in chat.messages after the anchor. An inactive version holds its whole tail (its user
// message and everything after it) and the chat-level context as it stood when it was put away.
// A branch point inside a stashed tail keeps its entry in chat.branches; its anchor id only
// renders again once that tail is live.
//
// Pure functions, no React: VerifierPage applies the returned chat through setChats.

export const MAX_VERSIONS = 10;

// Chat-level context the model and the search flows read. A version that ran a search leaves its
// filters here, so editing that search away without restoring these would let the old filters
// steer the new one.
const CTX_FIELDS = [
  'active_goal', 'active_constraints', 'research_context', 'context_summary',
  'seen_ref', 'enrichment_ref', 'companies_ref', 'company_research_ref',
];

// The full context, for a version being put away.
export function snapshotCtx(chat) {
  const snap = {};
  for (const f of CTX_FIELDS) snap[f] = chat?.[f] ?? null;
  snap.contacts_ref = Array.isArray(chat?.contacts_ref) ? chat.contacts_ref : null;
  return snap;
}

// The context just before a user turn runs, stamped on the user message as `ctxBefore`.
// contacts_ref is append-only (buildContactsRef) and the one field that grows large, so it is
// kept as a length and restored as a prefix of whatever the chat holds at the time.
export function markCtx(chat) {
  const snap = {};
  for (const f of CTX_FIELDS) snap[f] = chat?.[f] ?? null;
  snap.contactsLen = Array.isArray(chat?.contacts_ref) ? chat.contacts_ref.length : 0;
  return snap;
}

// Absent fields are deleted rather than set to null: several builders take the current ref as a
// defaulted parameter (buildSeenRef(..., existingRef = [])), and a default does not apply to null.
export function restoreCtx(chat, snap) {
  if (!snap) return chat;
  const next = { ...chat };
  for (const f of CTX_FIELDS) {
    if (snap[f] == null) delete next[f];
    else next[f] = snap[f];
  }
  const cur = Array.isArray(chat?.contacts_ref) ? chat.contacts_ref : [];
  if (Array.isArray(snap.contacts_ref)) next.contacts_ref = snap.contacts_ref;
  else if (snap.contactsLen > 0)        next.contacts_ref = cur.slice(0, snap.contactsLen);
  else                                  delete next.contacts_ref;
  return next;
}

export function anchorIdFor(messages, index) {
  return index > 0 ? String(messages[index - 1].id) : 'root';
}

// Where the tail after an anchor starts in the live path, or -1 when the anchor is not live.
function tailStart(messages, anchorId) {
  if (anchorId === 'root') return 0;
  const i = messages.findIndex(m => String(m.id) === anchorId);
  return i < 0 ? -1 : i + 1;
}

// Put the live tail from this user message away as a version, open a new live slot beside it, and
// cut the live path back to just before the message. The caller then sends the edited text, which
// lands in the new slot. Returns null when the message is not a live user message or the branch
// point is full.
export function forkChat(chat, userMsgId) {
  const msgs = chat?.messages || [];
  const i = msgs.findIndex(m => m.id === userMsgId);
  if (i < 0 || msgs[i].role !== 'user') return null;

  const anchorId = anchorIdFor(msgs, i);
  const tail     = msgs.slice(i);
  const existing = chat.branches?.[anchorId];
  if (existing && existing.versions.length >= MAX_VERSIONS) return null;

  const stashed  = { messages: tail, ctx: snapshotCtx(chat) };
  const versions = existing
    ? existing.versions.map((v, k) => (k === existing.active ? stashed : v))
    : [stashed];
  versions.push({ messages: null, ctx: null });

  let next = {
    ...chat,
    messages: msgs.slice(0, i),
    branches: { ...(chat.branches || {}), [anchorId]: { versions, active: versions.length - 1 } },
  };
  // Legacy messages carry no ctxBefore; the context then stays as it is, which is how every chat
  // behaved before versions existed.
  if (msgs[i].ctxBefore) next = restoreCtx(next, msgs[i].ctxBefore);
  delete next.agentTurn;
  return { chat: next, anchorId, removedIds: tail.map(m => m.id) };
}

// Show version k at a branch point: the live tail and context go into the active slot, version k's
// come out of its slot. Returns null when there is nothing to switch to.
export function switchVersion(chat, anchorId, k) {
  const b = chat?.branches?.[anchorId];
  if (!b || k === b.active || !Array.isArray(b.versions[k]?.messages)) return null;
  const msgs = chat.messages || [];
  const cut  = tailStart(msgs, anchorId);
  if (cut < 0) return null;

  const liveTail = msgs.slice(cut);
  const versions = b.versions.map((v, j) => (
    j === b.active ? { messages: liveTail, ctx: snapshotCtx(chat) }
    : j === k      ? { messages: null, ctx: null }
    : v
  ));
  let next = {
    ...chat,
    messages: [...msgs.slice(0, cut), ...b.versions[k].messages],
    branches: { ...chat.branches, [anchorId]: { versions, active: k } },
  };
  next = restoreCtx(next, b.versions[k].ctx);
  delete next.agentTurn;
  return { chat: next, removedIds: liveTail.map(m => m.id) };
}

// The `‹ k/n ›` arrows for the user message at this index, or null when it has no other versions.
export function branchInfoFor(chat, index) {
  const msgs = chat?.messages || [];
  if (msgs[index]?.role !== 'user') return null;
  const anchorId = anchorIdFor(msgs, index);
  const b = chat.branches?.[anchorId];
  if (!b || b.versions.length < 2) return null;
  return { anchorId, index: b.active, count: b.versions.length };
}

// Why this user message cannot be edited, or null when it can.
export function editBlockReason(chat, index) {
  const msgs = chat?.messages || [];
  const m = msgs[index];
  if (m?.role !== 'user') return 'Not a prompt';
  // The file is not kept on the message, so there is nothing to resend it with.
  if (m.attachmentName) return "Messages with a file can't be edited";
  // A card answer ("5", "go ahead") only means something to the card above it, and that card was
  // closed out in place when it was answered.
  if (m.answersCard) return "Answers to a card can't be edited";
  // Chip clicks and buttons echo a label as a user message; resending the label is not the action.
  if (m.synthetic) return "This step can't be edited";
  const b = chat.branches?.[anchorIdFor(msgs, index)];
  if (b && b.versions.length >= MAX_VERSIONS) return `Up to ${MAX_VERSIONS} versions per prompt`;
  return null;
}

// Anything a rerun could spend credits or vendor quota on. Those results are edited, not
// regenerated, so the spend is always a decision the user makes on the card.
const NOT_REGENERABLE = [
  'contacts', 'valid', 'risky', 'invalid', 'linkedinOnly', 'batchResults', 'enrichResults',
  'websiteRows', 'verifyEmails', 'lookalikeResults', 'companyListResults', 'researchData',
  'companyResearchData', 'isPrefQuestion', 'isSearchAsk', 'isIcpUpdate', 'isLookalikeAsk',
  'isCompaniesAsk', 'isVerifyConfirm', 'isDisambiguation', 'isLinkedInPrompt',
];
function hasPayload(m) {
  return NOT_REGENERABLE.some(f => (Array.isArray(m[f]) ? m[f].length > 0 : !!m[f]));
}

// The user message a regenerate would resend for the assistant message at this index, or null
// when that reply can't be regenerated. Only the last reply of a turn offers it, and only a
// finished text reply whose prompt could itself be edited.
export function regenerateSource(chat, index) {
  const msgs = chat?.messages || [];
  const m = msgs[index];
  if (m?.role !== 'assistant' || !m.done || !m.isConversational || hasPayload(m)) return null;
  if (msgs[index + 1] && msgs[index + 1].role !== 'user') return null;
  let u = index - 1;
  while (u >= 0 && msgs[u].role !== 'user') u--;
  if (u < 0 || editBlockReason(chat, u)) return null;
  return msgs[u];
}
